import { createRoute, Navigate } from '@tanstack/react-router'
import { rootRoute } from './__root'
import { useSession, signIn } from '@/lib/auth'
import { Button } from '@/components/ui/button'
import { RiDiscordFill } from '@remixicon/react'

export const signInRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/sign-in',
	component: SignInPage,
})

function SignInPage() {
	const { data: session, isPending } = useSession()

	const handleDiscordLogin = async () => {
		await signIn.social({
			provider: 'discord',
			errorCallbackURL: '/unauthorized',
		})
	}

	if (isPending) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center">
				<div className="text-muted-foreground">Loading...</div>
			</div>
		)
	}

	if (session) {
		return <Navigate to="/" />
	}

	return (
		<main className="relative min-h-screen bg-background flex flex-col items-center justify-center overflow-hidden">
			{/* Atmospheric background layers */}
			<div
				className="pointer-events-none absolute inset-0"
				style={{
					background: `
						radial-gradient(ellipse 60% 40% at 50% 0%, oklch(0.77 0.16 70 / 6%) 0%, transparent 100%),
						radial-gradient(ellipse 50% 60% at 20% 80%, oklch(0.65 0.2 293 / 4%) 0%, transparent 100%),
						radial-gradient(ellipse 50% 50% at 80% 70%, oklch(0.7 0.15 162 / 3%) 0%, transparent 100%)
					`,
				}}
			/>

			{/* Subtle grid pattern */}
			<div
				className="pointer-events-none absolute inset-0 opacity-[0.03]"
				style={{
					backgroundImage: `
						linear-gradient(oklch(1 0 0 / 100%) 1px, transparent 1px),
						linear-gradient(90deg, oklch(1 0 0 / 100%) 1px, transparent 1px)
					`,
					backgroundSize: '64px 64px',
				}}
			/>

			{/* Content */}
			<div className="relative z-10 flex flex-col items-center w-full max-w-xs mx-4">
				{/* Logo */}
				<div className="mb-10 flex flex-col items-center">
					<div className="relative flex size-16 items-center justify-center mb-5">
						<div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/25 via-primary/8 to-transparent" />
						<div className="absolute inset-1.5 rounded-full bg-background" />
						<svg
							viewBox="0 0 24 24"
							className="relative z-10 size-7 fill-primary"
							aria-hidden="true"
						>
							<path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
						</svg>
					</div>
					<h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">
						Adagio
					</h1>
					<p className="mt-1.5 text-[11px] uppercase tracking-[0.25em] text-muted-foreground/50">
						Music Bot
					</p>
				</div>

				{/* Sign-in card */}
				<div className="w-full space-y-5">
					<div className="text-center">
						<p className="text-sm text-muted-foreground">
							Connect your Discord account to continue
						</p>
					</div>
					<Button
						onClick={handleDiscordLogin}
						className="w-full h-9"
					>
						<RiDiscordFill className="size-4" />
						Login with Discord
					</Button>
				</div>
			</div>

			{/* Bottom fade line */}
			<div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-border/30 to-transparent" />
		</main>
	)
}
